import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { never } from "client_legacy/ndaw/errors";
import * as culture from "client_legacy/ndaw/culture";
import * as path from "./path";

// To have less duplicated code we hack the extension to be .one to match
// OneNote sections
export const ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION = ".one";

export type FileThumbnailKind =
	| "archive"
	| "calendar"
	| "csv"
	| "folder"
	| "create-folder"
	| "message"
	| "jpeg"
	| "png"
	| "gif"
	| "bmp"
	| "doc"
	| "docx"
	| "docm"
	| "dotx"
	| "dotm"
	| "visio"
	| "xls"
	| "xlsx"
	| "xlsm"
	| "powerpoint"
	| "pptm"
	| "onenote"
	| "file"
	| "text"
	| "pdf"
	| "create-word"
	| "create-excel"
	| "create-powerpoint"
	| "create-onenote";

function getSpriteClassForExtension(extension: string): FileThumbnailKind {
	let extensionToUse: string = extension?.trim()
		?.toLowerCase() ?? "";

	// Officially a file extension does not contain the "full stop", though a lot of our code (including
	// the path module (which is based on .NET)) returns the "full stop". To make it easier to use this
	// function for system which might not include the "full stop", we prepend it.
	if (extensionToUse.length > 0 && extensionToUse[0] !== ".") {
		extensionToUse = "." + extensionToUse;
	}

	switch (extensionToUse) {
		case ".zip":
		case ".rar":
		case ".tar.gz":
		case ".7z":
		case ".7zip":
			return "archive";
		case ".jpg":
		case ".jpeg":
			return "jpeg";
		case ".bmp":
			return "bmp";
		case ".png":
			return "png";
		case ".gif":
			return "gif";
		case ".doc":
			return "doc";
		case ".docx":
			return "docx";
		case ".dotx":
			return "dotx";
		case ".docm":
			return "docm";
		case ".dotm":
			return "dotm";
		case ".xlsm":
			return "xlsm";
		case ".xls":
		case ".xlsb":
		case ".csv":
			return "xls";
		case ".xlsx":
			return "xlsx";
		case ".potx":
		case ".ppt":
		case ".pptx":
			return "powerpoint";
		case ".pptm":
			return "pptm";
		case ".one":
			return "onenote";
		case ".txt":
			return "text";
		case ".pdf":
			return "pdf";
		case ".vsd":
		case ".vsdx":
			return "visio";
		case ".eml":
		case ".msg":
			return "message";
		default:
			return "file";
	}
}

interface ExtensionAndType {
	extension: string;
	type: FileThumbnailKind;
}

function getExtensionAndType(itemName: string, isFolder?: boolean): ExtensionAndType {
	let extension: string;
	let type: FileThumbnailKind;

	if (isFolder) {
		extension = "";
		type = "folder";
	} else {
		extension = path.getExtension(itemName);
		type = getSpriteClassForExtension(extension);
	}

	return {
		extension: extension,
		type: type
	};
}

function getDisplayFriendlySize(size: number | nil): string {
	if (typeof size === "undefined" || size === null) {
		return "";
	}

	if (size < 1024) {
		return size + " " + culture.localize("_Bytes");
	}

	const kilobytes = size / 1024;

	if (kilobytes < 1024) {
		return kilobytes.toFixed(2) + " " + culture.localize("_KB");
	}

	const megabytes = kilobytes / 1024;

	if (megabytes < 1024) {
		return megabytes.toFixed(2) + " " + culture.localize("_MB");
	}

	const gigabytes = megabytes / 1024;

	if (gigabytes < 1024) {
		return gigabytes.toFixed(2) + " " + culture.localize("_GB");
	}

	return gigabytes.toFixed(0) + culture.localize("_GB");
}

function getDocumentKind(
	documentType: DocumentItemType,
	fileExtension: string
): FileThumbnailKind {
	switch (documentType) {
		case DocumentItemType.File:
			return getSpriteClassForExtension(fileExtension);
		case DocumentItemType.OneNoteNotebook:
			return "onenote";
		case DocumentItemType.Folder:
			return "folder";
		default:
			never(documentType);
	}
}

export {
	getSpriteClassForExtension,
	getDisplayFriendlySize,
	getExtensionAndType,
	type ExtensionAndType,
	getDocumentKind
};
